import { SearchIcon } from 'lucide-react';
import { convertQwertyToHangul, disassemble } from 'es-hangul';
import _ from 'lodash';
import MiniSearch, { SearchOptions } from 'minisearch';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { NestedKeyOf } from '../../../types';

type SearchableConfig<T> = {
    idField?: string;
    fields: NestedKeyOf<T>[];
    searchOptions?: SearchOptions;
};

export class Searchable<T = any> {
    private miniSearch: MiniSearch<T> | undefined;
    private config: SearchableConfig<T>;
    /**
     * 원본 데이터
     */
    private items: T[] = [];
    /**
     * 원본 데이터 아이디 맵
     */
    private itemMap: Record<string, T> = {};

    /**
     * 검색 결과
     */
    searchedItems: T[] = [];

    query: string = '';

    constructor(config: SearchableConfig<T>) {
        makeAutoObservable(this);
        this.config = _.defaults(config, {
            idField: 'hash',
            fields: [],
        });
    }

    private initMiniSearch() {
        /**
         * 검색 엔진
         * @see https://github.com/lucaong/minisearch
         */
        this.miniSearch = new MiniSearch({
            idField: this.config.idField,
            // 인덱스를 생성할 필드
            fields: this.config.fields,
            // 응답에 포함할 필드
            storeFields: [this.config.idField as string],
            searchOptions: _.defaults(this.config.searchOptions, {
                // MiniSearch 는 기본 풀텍스트 검색이지만 서비스 내에서는 거의 키워드 검색으로 사용
                // 따라서 prefix 검색 활성화
                prefix: true,
                // 검색 결과 중 일치하는 항목이 없을 때 유사도 검색 활성화
                fuzzy: 0.2,
            }),

            extractField: (document, fieldName) => {
                // 'foo.bar' 같은 중첩된 값을 가져옴
                return _.get(document, fieldName);
            },

            /**
             * 용어 정규화
             */
            processTerm: (term) => {
                const defaultProcessTerm = MiniSearch.getDefault('processTerm');

                let normalizedTerm = defaultProcessTerm(term);
                // 한글 자소 분리해서 저장함
                normalizedTerm = disassemble(normalizedTerm);

                return normalizedTerm;
            },
        });

        this.searchedItems = this.items;
        this.miniSearch.addAll(this.items);
    }

    setItems(items: T[]) {
        this.items = items;
        this.itemMap = _.keyBy(items, this.config.idField);

        // 데이터가 업데이트 되었을 때 초기 검색 결과는 원본과 동일하게 설정
        this.searchedItems = items;

        if (this.miniSearch) {
            this.miniSearch.removeAll();
            this.miniSearch.addAll(items);
        }
    }

    search(query: string): void {
        const refinedQuery = (query || '').trim().replace(/\s+/g, ' ');

        if (!refinedQuery) {
            this.query = '';
            this.searchedItems = this.items;
            return;
        }

        if (this.query === refinedQuery) {
            return;
        }

        // 미니서치는 실제로 검색을 실행할 때 초기화한다
        if (!this.miniSearch) {
            this.initMiniSearch();
        }

        this.query = refinedQuery;

        let searched = this.miniSearch!.search(refinedQuery);

        // 2벌식으로 다시 검색해볼지 여부
        const needRetryWithQwerty =
            _.isEmpty(searched) && refinedQuery?.length >= 2 && /^[a-z]+$/i.test(refinedQuery);

        if (needRetryWithQwerty) {
            const qwertyQuery = convertQwertyToHangul(refinedQuery);
            searched = this.miniSearch!.search(qwertyQuery);
        }

        // 검색 결과에서 키 값으로 찾아 결과 데이터를 반환
        this.searchedItems = _.map(searched, (result) => {
            return this.itemMap[result.id];
        });
    }
}

/**
 * Searchable 을 사용하는 검색 인풋
 */
const SearchInput = observer(
    <T = unknown,>({
        placeholder = '검색',
        className,
        searchable,
    }: {
        placeholder?: string;
        className?: string;
        searchable: Searchable<T>;
    }) => {
        const [isScrolled, setIsScrolled] = useState(false);

        return (
            <label className={twMerge('input w-full', className)}>
                <SearchIcon className="text-base-content/50 size-4" />
                <input
                    type="search"
                    value={searchable.query}
                    style={{
                        scrollMarginTop: `calc(var(--navbar-height) + 15px)`,
                    }}
                    placeholder={placeholder}
                    onInput={(e) => {
                        const query = e.currentTarget.value;
                        searchable.search(query);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.currentTarget.blur();
                            return;
                        }

                        if (!isScrolled) {
                            // 키를 입력하면 스크롤 상단으로 고정해줌
                            e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            setIsScrolled(true);
                        }
                    }}
                    onBlur={() => {
                        setIsScrolled(false);
                    }}
                />
            </label>
        );
    },
);

export const SearchNoResult = observer(({ className }: { className?: string }) => {
    return (
        <div
            className={twMerge(
                'text-base-content/50 flex items-center justify-center px-5 py-10 text-sm',
                className,
            )}
        >
            검색 결과가 없습니다.
        </div>
    );
});

export default SearchInput;
